/**
 * SupportCenter - Create and manage support tickets
 * Phase 11 Part 2: Support System
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import ChatWindow from '../components/ChatWindow';
import { 
  ArrowLeft, 
  Headphones, 
  Plus,
  Package,
  CreditCard,
  AlertTriangle,
  HelpCircle,
  MessageCircle,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  Loader2,
  Send
} from 'lucide-react';

// Ticket categories
const CATEGORIES = [
  { id: 'delivery_issue', label: 'Delivery Issue', icon: Package, color: 'text-blue-500' },
  { id: 'payment_issue', label: 'Payment Issue', icon: CreditCard, color: 'text-green-500' },
  { id: 'order_issue', label: 'Order Issue', icon: Package, color: 'text-purple-500' },
  { id: 'fraud_suspicion', label: 'Report Fraud', icon: AlertTriangle, color: 'text-red-500' },
  { id: 'app_support', label: 'App Support', icon: HelpCircle, color: 'text-gray-500' },
  { id: 'other', label: 'Other', icon: MessageCircle, color: 'text-gray-400' }
];

// Status colors
const STATUS_COLORS = {
  open: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Open' },
  in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'In Progress' },
  resolved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Resolved' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Closed' }
};

// Priority colors
const PRIORITY_COLORS = {
  low: { bg: 'bg-gray-100', text: 'text-gray-600' },
  medium: { bg: 'bg-blue-100', text: 'text-blue-600' },
  high: { bg: 'bg-orange-100', text: 'text-orange-600' },
  critical: { bg: 'bg-red-100', text: 'text-red-600' }
};

export default function SupportCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('tickets'); // tickets, new, chat
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // New ticket form
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [relatedOrderId, setRelatedOrderId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await api.get('/support/tickets/mine');
      setTickets(res.data.tickets);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!category || !subject || !description) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post('/support/tickets', {
        category,
        subject,
        description,
        related_order_id: relatedOrderId || null
      });
      
      toast.success('Support ticket created!');
      setSelectedTicket(res.data.ticket);
      setActiveTab('chat');
      
      // Reset form
      setCategory('');
      setSubject('');
      setDescription('');
      setRelatedOrderId('');
      
      // Refresh tickets
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#00A86B] to-[#00A86B]/90 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => activeTab === 'chat' ? setActiveTab('tickets') : navigate(-1)}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Headphones className="w-6 h-6" />
                Support Center
              </h1>
              <p className="text-sm text-white/70">
                We're here to help
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        {activeTab !== 'chat' && (
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'tickets' ? 'default' : 'outline'}
              onClick={() => setActiveTab('tickets')}
              className={activeTab === 'tickets' ? 'bg-[#00A86B]' : ''}
            >
              My Tickets ({tickets.length})
            </Button>
            <Button
              variant={activeTab === 'new' ? 'default' : 'outline'}
              onClick={() => setActiveTab('new')}
              className={activeTab === 'new' ? 'bg-[#00A86B]' : ''}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
        )}

        {/* Tickets List */}
        {activeTab === 'tickets' && (
          <div className="space-y-4">
            {tickets.length === 0 ? (
              <Card className="p-12 text-center">
                <Headphones className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Support Tickets</h3>
                <p className="text-gray-500 mb-4">Need help? Create a support request.</p>
                <Button 
                  onClick={() => setActiveTab('new')}
                  className="bg-[#00A86B] hover:bg-[#00A86B]/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Ticket
                </Button>
              </Card>
            ) : (
              tickets.map(ticket => {
                const Cat = CATEGORIES.find(c => c.id === ticket.category);
                const CatIcon = Cat?.icon || HelpCircle;
                const statusStyle = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
                const priorityStyle = PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.medium;
                
                return (
                  <Card
                    key={ticket.id}
                    onClick={() => { setSelectedTicket(ticket); setActiveTab('chat'); }}
                    className="p-4 cursor-pointer hover:border-[#00A86B]/50 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center`}>
                        <CatIcon className={`w-6 h-6 ${Cat?.color || 'text-gray-500'}`} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-800">{ticket.subject}</h3>
                          <Badge className={`${statusStyle.bg} ${statusStyle.text}`}>
                            {statusStyle.label}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-gray-500 mt-1">#{ticket.id.slice(0, 8)}</p>
                        
                        <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(ticket.created_at)}
                          </span>
                          <Badge className={`${priorityStyle.bg} ${priorityStyle.text} text-xs`}>
                            {ticket.priority}
                          </Badge>
                          {ticket.message_count > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageCircle className="w-3 h-3" />
                              {ticket.message_count} messages
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* New Ticket Form */}
        {activeTab === 'new' && (
          <Card className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-6">Create Support Request</h2>
            
            <form onSubmit={handleCreateTicket} className="space-y-6">
              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What can we help you with?
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    const isSelected = category === cat.id;
                    return (
                      <div
                        key={cat.id}
                        onClick={() => setCategory(cat.id)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-[#00A86B] bg-[#00A86B]/5' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-6 h-6 ${cat.color} mb-2`} />
                        <p className="text-sm font-medium text-gray-800">{cat.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of your issue"
                  className="border-gray-200"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A86B] focus:border-transparent resize-none text-black"
                  required
                />
              </div>

              {/* Related Order (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Related Order ID (optional)
                </label>
                <Input
                  value={relatedOrderId}
                  onChange={(e) => setRelatedOrderId(e.target.value)}
                  placeholder="If this is about a specific order"
                  className="border-gray-200"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting || !category || !subject || !description}
                className="w-full bg-[#00A86B] hover:bg-[#00A86B]/90 h-12"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </Card>
        )}

        {/* Chat View */}
        {activeTab === 'chat' && selectedTicket && (
          <div className="space-y-4">
            {/* Ticket Info */}
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">{selectedTicket.subject}</h3>
                  <p className="text-sm text-gray-500">#{selectedTicket.id.slice(0, 8)}</p>
                </div>
                <Badge className={`${STATUS_COLORS[selectedTicket.status]?.bg} ${STATUS_COLORS[selectedTicket.status]?.text}`}>
                  {STATUS_COLORS[selectedTicket.status]?.label}
                </Badge>
              </div>
            </Card>

            {/* Chat */}
            <ChatWindow
              conversationId={selectedTicket.conversation_id}
              title={`Support: ${selectedTicket.subject}`}
              className="h-[500px]"
            />
          </div>
        )}
      </main>
    </div>
  );
}
